<template>
  <RDialog
    :activator="activator"
    title="Device Dialog"
    maxWidth="99vw"
    :width="isDeviceDialog ? null : '700px'"
    persistent
    :showProgressBar="showProgressBar"
  >
    <template #content>
      <v-card-text>
        <v-row v-if="localDeviceCall && isImmediate || isStoreCard" class="px-1">
          <v-col class="pb-0">
            <span>Waiting on device response. Please follow instructions on the device.</span>
          </v-col>
        </v-row>
        <v-row v-else-if="asyncOrderPayment" class="px-1">
          <v-col v-if="netReadersEnabled && isCard" class="pb-0 font-weight-bold" cols="12">
            Sending {{ paymenttype.description }} for {{ formatCurrency(payment.amount) }} to external device.
          </v-col>
          <v-col v-else-if="isCheck" class="pb-0 font-weight-bold" cols="12">
            Authorizing {{ paymenttype.description }} payment for {{ formatCurrency(payment.amount) }}.
          </v-col>
          <v-col v-else class="pb-0 font-weight-bold" cols="12">
            Processing {{ paymenttype.description }} payment for {{ formatCurrency(payment.amount) }}.
          </v-col>
          <v-spacer />
          <v-col class="pb-0" cols="12">
            Please wait...
          </v-col>
        </v-row>
        <v-row v-else-if="asyncStatus" class="px-1">
          <v-col class="pb-0 font-weight-bold" cols="12">
            Waiting on response from external device.
          </v-col>
          <v-col class="pb-0" cols="12">
            {{ paymenttype.description }} for {{ formatCurrency(payment.amount) }}
          </v-col>
        </v-row>
        <v-row v-if="prompts && !!prompts.length">
          <v-col cols="12" class="pb-1">
            <span v-if="showProgressBar">Waiting on device response. Please follow instructions on the device.</span>
          </v-col>
          <v-col v-if="currentPrompt && dialogInFlight" cols="12" class="py-0">
            <v-card variant="outlined">
              <v-card-title class="pt-0 pb-1 text-subtitle-1 font-weight-bold">Current Prompt</v-card-title>
              <v-card-subtitle class="pb-1 text-subtitle-2 font-weight-regular">Dialog Type: {{ currentPrompt.type }}</v-card-subtitle>
              <v-card-text class="py-1 text-body-2">{{ currentPrompt.promptString }}</v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" class="pa-0">
            <DeviceDialogPrompts :prompts="responses" />
          </v-col>
        </v-row>
        <v-row v-if="prompts && !!prompts.length">
          <v-col cols="12">
            <SmartTerminalSelect
              v-if="isDeviceDialog && netReadersEnabled"
              :model="model"
              :hasCol="false"
              @update-value="updateForm"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </template>
    <template #actions>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="waitingForWelcomeScreen"
          text
          @click="cancel()"
        >
          Cancel
        </v-btn>
        <v-btn
          v-if="isDeviceDialog && !netReadersEnabled && readyToSave"
          color="primary"
          :disabled="showProgressBar || waitingForWelcomeScreen"
          variant="text"
          @click="restartConversation()"
        >
          Restart Dialog
        </v-btn>
        <v-btn
          v-if="isDeviceDialog && netReadersEnabled && readyToSave"
          color="primary"
          :disabled="showProgressBar"
          variant="text"
          @click="startConversation()"
        >
          Restart Dialog
        </v-btn>
        <v-btn
          v-if="isDeviceDialog && netReadersEnabled && devicePromptBypassPerm && !readyToSave"
          color="primary"
          :disabled="showProgressBar"
          variant="text"
          @click="acceptConversation()"
        >
          Skip Dialog
        </v-btn>
        <v-btn
          v-if="isDeviceDialog"
          color="primary"
          variant="elevated"
          :disabled="showProgressBar || responseErrors && !devicePromptBypassPerm"
          @click="startOrAcceptConversation()"
        >
          {{ dialogAcceptLabel }}
        </v-btn>
      </v-card-actions>
    </template>
  </RDialog>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import RDialog from '@/components/core/common/RDialog.vue';
import { formatCurrency } from '@/lib/globalRegistrations';
import { post, imageFromMagTekSignature, openReportOutputs } from '@/lib/utils';
import SmartTerminalSelect from '@/components/core/common/SmartTerminalSelect.vue';
import { payJunctionMixin } from '@/mixins/payJunctionMixin';

/* istanbul ignore next */
const DeviceDialogPrompts = defineAsyncComponent(() => import('@/components/core/common/DeviceDialogPrompts.vue'));

export default {
  components: {
    RDialog,
    DeviceDialogPrompts,
    SmartTerminalSelect,
  },
  mixins: [payJunctionMixin],
  props: {
    activator: {
      type: Boolean,
      required: true,
    },
    model: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      readyToSave: false,
      responseErrors: false,
      showProgressBar: false,
      waitingForWelcomeScreen: false,
      pollCount: 0,
    };
  },
  watch: {
    async activator(newVal) {
      if (newVal === true) {
        if (this.localDeviceCall && this.isImmediate) {
          await this.getDeviceData();
        } else if (this.asyncOrderPayment) {
          await this.processAsyncPayment();
        } else if (this.requestType === 'asyncRequestDialog') {
          if (!this.isStoreCard) {
            // TODO: FIX LINT
            // eslint-disable-next-line vue/no-mutating-props
            this.model.requestData = { dialogData: this.model.async_params.dialogprompts, requestType: 'userdialog' };
            // eslint-disable-next-line vue/no-mutating-props
            this.model.requestType = 'get';
          } else {
            // eslint-disable-next-line vue/no-mutating-props
            this.model.requestData.dialogData = this.model.async_params.dialogprompts;
            // eslint-disable-next-line vue/no-mutating-props
            this.model.requestData.requestType = 'userdialog';
          }
          if (this.netReadersEnabled) {
            // eslint-disable-next-line vue/no-mutating-props
            if (!this.model.selectedSmartTerminal) this.model.selectedSmartTerminal = this.selectedSmartTerminal;
            if (this.isStoreCard) await this.startAsyncPrompt({ resHandler: this.promptStatusResHandler, isDialogModal: true });
          } else {
            await this.getDeviceData();
          }
        }
      }
    },
    async model(newModel) {
      if (!newModel) return;
      if (newModel.requestType === 'asyncstatus') {
        this.pollCount = 0;
        await this.processPaymentStatus();
      }
      else if (newModel.requestType === 'asyncqueue') {
        await this.processAsyncPayment();
      }
    },
  },
  computed: {
    dialogAcceptLabel() {
      if (!this.readyToSave) return 'Start Dialog';
      return 'Accept';
    },
    devicePromptBypassPerm() {
      return this.$store.getters['permissions/isPermPresent']('devicepromptbypass');
    },
    isImmediate() {
      return this.model && this.model.immediate;
    },
    isDeviceDialog() {
      return !!_get(this, 'prompts.length');
    },
    requestType() {
      return this.model && this.model.requestType;
    },
    isStoreCard() {
      return _get(this, 'model.deviceConfig.storecard');
    },
    localDeviceCall() {
      return this.model && ['get', 'getEMV'].includes(this.requestType);
    },
    asyncOrderPayment() {
      return this.model && ['asyncqueue', 'asyncordertrans'].includes(this.requestType);
    },
    isCard() {
      return this.paymenttype && this.paymenttype.paymenttypetype === 'BC';
    },
    isCheck() {
      return this.paymenttype && this.paymenttype.paymenttypetype === 'CK';
    },
    asyncStatus() {
      return this.model && this.requestType === 'asyncstatus';
    },
    payment() {
      if (this.asyncOrderPayment) {
        return _get(this.model, 'async_params.trans[0].ArTran');
      }
      if (this.asyncStatus) {
        return _get(this.model, 'async_params.async_params.ArTran');
      }
      if (this.localDeviceCall) {
        return this.model.requestData;
      }
      return {};
    },
    paymenttype() {
      return _get(this.payment, 'paymenttype', {});
    },
    prompts() {
      const prompts = _cloneDeep(_get(this, 'model.async_params.dialogprompts', []));
      if (prompts.find(p => p.type === 'STORECARD')) return [];
      return prompts;
    },
    responses() {
      return _get(this, 'model.requestData.dialogData', []);
    },
    currentPrompt() {
      let promptData = null;
      const currentPrompt = _get(this, 'model.requestData.currentprompt');
      if (currentPrompt) {
        promptData = {
          promptString: currentPrompt.promptString,
          type: this.$store.getters['translations/label'](`{DeviceDialog.type.${currentPrompt.type}}l`),
        };
      }
      return promptData;
    },
    ...mapGetters({
      cardReader: 'auth/cardReader',
      chipReader: 'auth/chipReader',
      cardReadersEnabled: 'auth/cardReadersEnabled',
      chipReadersEnabled: 'auth/chipReadersEnabled',
      netReadersEnabled: 'auth/netReadersEnabled',
      paymentGateway: 'auth/paymentGateway',
      selectedSmartTerminal: 'auth/selectedSmartTerminal',
    }),
  },
  methods: {
    formatCurrency,
    toggleDialog(bool) {
      this.showProgressBar = false;
      this.readyToSave = false;
      this.$store.dispatch('auth/toggleDeviceDialogModal', { showModal: bool });
    },
    async getDeviceData() {
      // only for MagTek devices
      this.showProgressBar = true;
      this.responseErrors = false;

      const requestParams = {
        requestType: this.model.requestType,
        requestData: this.model.requestData,
        deviceConfig: this.model.deviceConfig,
      };
      const result = await this.$store.dispatch('auth/deviceIntraface', requestParams);
      if (!_isEmpty(result)) {
        // TODO: FIX LINT
        // eslint-disable-next-line vue/no-mutating-props
        this.model.requestData.creditcarddata = result;
        const dialogData = result.dialogData;
        if (dialogData) {
          dialogData.forEach((dialog) => {
            if (_get(dialog, '_type_errors')) {
              this.responseErrors = true;
            }
            if (dialog.type === 'sign' && imageFromMagTekSignature(dialog)) {
              dialog.promptString = 'signature captured';
            }
          });
          // TODO: FIX LINT
          // eslint-disable-next-line vue/no-mutating-props
          this.model.requestData.dialogData = dialogData;
          this.readyToSave = true;
        }
        if (this.isImmediate) {
          this.toggleDialog(false);
        }
      } else {
        this.showProgressBar = false;
        // NOTE: This skips messages where a read status is present and so is a message (see comment in PaymentModal.vue).
        if (!result.readstatus && result.message) {
          this.$store.dispatch('notify/push', { title: result.message });
        }
      }
      this.showProgressBar = false;
      if (!_isEmpty(result)) {
        this.waitingForWelcomeScreen = true;
        // Wait 5.25 seconds for Thank You screen to clear and MagTek device to become available.
        // Otherwise, LoftNode will crash and need to be reset if device is unavailable when polled.
        // This pertains to Restart Dialog & Cancel buttons because if modal gets cancelled and
        // reopened during display of Thank You screen, it will also crash LoftNode.
        await new Promise(r => { setTimeout(r, 5250); });
        this.waitingForWelcomeScreen = false;
      }
    },
    async processAsyncPayment() {
      this.showProgressBar = true;
      const payload = await post('/tg/artran/save_async_order_payment',
        { async_params: this.model.async_params },
        true);
      if (!(this.isCard && this.netReadersEnabled) && !_get(payload, 'response.asyncrequests')) {
        const model = payload.results[0];
        if (model._classname === 'ArTran') {
          this.$store.dispatch('arTran/setModel', { model, parentPath: 'record' });
        } else {
          this.$store.dispatch('editObject/updateRecord', model);
        }
        this.toggleDialog(false);
      }
    },
    async processPaymentStatus() {
      // differs from prompt status...
      this.showProgressBar = true;
      const payload = await post('/tg/smartterminal/async_payment_status', {
        requestid: this.model.async_params.requestid,
        async_params: this.model.async_params,
        poll_count: this.pollCount,
      }, true);
      const self = this;
      if (payload.response.pending) {
        this.pollCount = payload.response.poll_count;
        setTimeout(() => {
          self.processPaymentStatus();
        }, 3000);
      } else if (payload.response.asyncrequests) {
        this.pollCount = 0;
      } else {
        const model = payload.results[0];
        if (model) {
          if (model._classname === 'ArTran') {
            this.$store.dispatch('arTran/setModel', { model, parentPath: 'record' });
          } else {
            this.$store.dispatch('editObject/updateRecord', model);
          }
        }
        this.toggleDialog(false);
      }
    },
    promptResponseResHandler() {
      const responses = this.responses;
      if (responses) {
        responses.forEach((dialog) => {
          if (dialog._response_errors) {
            this.responseErrors = true;
          }
        });
      }
    },
    promptStatusResHandler() {
      this.readyToSave = true;
      this.toggleDialog(false);
    },
    async startConversation() {
      this.responseErrors = false;
      return this.startAsyncPrompt({ resHandler: this.promptResponseResHandler, requiresPrompts: true, isDialogModal: true });
    },
    async startOrAcceptConversation() {
      if (!this.readyToSave) {
        return this.startConversation();
      }
      this.acceptConversation();
    },
    async acceptConversation() {
      // NOTE: Skipping the dialog also enters flow here...
      if (!this.readyToSave && !this.devicePromptBypassPerm) {
        this.$store.dispatch('notify/push', {
          text: 'Unable to skip dialog, missing permission "devicepromptbypass".',
        });
        return;
      }

      if (this.isStoreCard) {
        this.toggleDialog(false);
        return;
      }

      this.showProgressBar = true; // disable buttons...
      const params = {
        ...this.model.async_params,
        dialogcomplete: this.readyToSave,
        dialogdevicepresent: true,
        dialogprompts: this.prompts,
        dialogresults: this.responses,
      };

      const { results, response } = await this.$store.dispatch('editObject/post', { url: '/tg/report/runreport', params, returnResponse: true });

      openReportOutputs(results, response);

      this.showProgressBar = false; // enable buttons...
      // it is an acceptable response to have no report outputs (i.e. results.length == 0)
      // we will close the dialog if there are no errors reported from the server
      if (results.length || !_get(response, 'err_msg')) this.toggleDialog(false);
    },
    async restartConversation() {
      // only for MagTek devices
      this.showProgressBar = true;
      // TODO: FIX LINT
      // eslint-disable-next-line vue/no-mutating-props
      this.model.requestData = { dialogData: this.prompts, requestType: 'userdialog' };
      this.getDeviceData();
    },
    async cancel() {
      if (this.showProgressBar && !this.netReadersEnabled) {
        // only for MagTek devices
        await this.$store.dispatch('auth/deviceIntraface', {
          requestData: { ...this.model.requestData, selectedCardReader: this.cardReader },
          requestType: 'cancel',
        });
      }

      this.clearTimer();
      this.showProgressBar = false;
      this.responseErrors = false;
      this.toggleDialog(false);
    },
    updateForm(payload) {
      if (payload.key === 'smartTerminalId' && payload.value) {
        if (this.selectedSmartTerminal !== payload.value) {
          this.$store.dispatch('auth/setSmartTerminal', payload.value);
        }
        // TODO: FIX LINT
        // eslint-disable-next-line vue/no-mutating-props
        this.model.smartTerminalId = payload.value;
      }
    },
  },
};
</script>
